// Per spec/design-system.md §Primitive 8. Bulletproof table-based structure
// for Outlook compatibility: outer <table> → <tr> → <td bgcolor + radius>
// with an inner <a> that holds the visible padding/typography. Outlook ignores
// border-radius on both the cell and anchor; degradation accepted per
// design-system.md §Tokens. fontFamily is interpolated through `style="..."`
// and HTML-escapes to its encoded byte form per D-029.
//
// **P-037.26 D-134 — `toAbsoluteUrl` applied to `href`.** Same defense-
// in-depth as the link primitive: a relative path threaded into a CTA
// button emits as an absolute URL in the rendered HTML so CC + View-in-
// Tab can resolve it. Absolute URLs + CC variables pass through
// unchanged.
//
// **P-037.30 eighth post-Phase-Close §A — `align?: 'left' | 'center'`
// prop.** The button primitive's outer `<table>` is shrink-wrapped to
// its inner content (no width attribute) — by default it sits left-
// aligned within whatever parent cell wraps it. When `align: 'center'`
// is passed, the outer `<table>` gets two centering layers mirroring
// the D-134 `document-shell.ts` belt-and-suspenders pattern: `align=
// "center"` HTML attribute (Outlook's Word rendering engine reads this
// to center fixed/shrink-wrap-width tables relative to the parent
// block) + `style="margin: 0 auto;"` inline style (modern clients
// respect this on block-level tables). Default stays `'left'` for
// backward compat — `feature-card.ts`'s call site doesn't pass `align`
// and renders unchanged. The `button` Block (`src/blocks/button.ts`)
// passes `align: 'center'` per Banks's P-037.30 audit pick (reverses
// the D-136 planning decision that locked all 4 atomic Blocks to
// LEFT-aligned).
//
// **P-048.1 D-144 (Item 4) — belt-and-suspenders bg-color.** Banks's
// real-send-to-Gmail smoke showed the button rendering as faded text
// without the rounded-box bg — the cell's `bgcolor="…"` HTML attribute
// alone was insufficient in Gmail's stripped-`<style>` render path.
// Mirror the D-134 / footer.ts pattern: add `background-color: ${bgcolor};`
// inline on the cell `<td>` in ADDITION to the existing `bgcolor=` HTML
// attr. Two-layer bg: `bgcolor` for Outlook desktop's Word renderer +
// inline `background-color` for modern browsers / Gmail web / iOS Mail
// / Apple Mail. Existing inline `display: inline-block; padding: …;
// font-family: …; font-weight: …; color: …; text-decoration: none;
// border-radius: …;` on the `<a>` already covered the typography +
// padding + decoration surface — the bg was the only declaration
// previously relying solely on cell-level `bgcolor`.

import { html, toAbsoluteUrl } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

export interface ButtonProps {
  text: string
  href: string
  bgcolor?: string
  textColor?: string
  paddingX?: number
  paddingY?: number
  borderRadius?: number
  /**
   * Horizontal alignment of the button table within its parent cell.
   * Default `'left'` matches the historical behavior (shrink-wrapped
   * table sits left-aligned with no `align=` attribute or `margin`
   * style on the outer table). `'center'` adds `align="center"` HTML
   * attribute + `margin: 0 auto;` inline style to the outer table —
   * Outlook + modern-client belt-and-suspenders centering.
   */
  align?: 'left' | 'center'
}

export function button({
  text,
  href,
  bgcolor = tokens.color['accent-coral'],
  textColor = tokens.color['text-on-dark'],
  paddingX = tokens.space[6],
  paddingY = tokens.space[3],
  borderRadius = tokens.radius.sm,
  align = 'left',
}: ButtonProps): HtmlString {
  const absoluteHref = toAbsoluteUrl(href)
  // P-048.1 D-144 (Item 4) — `background-color` inline on the cell in
  // addition to the `bgcolor=` HTML attr. Two-layer bg matches the
  // D-134 / footer.ts belt-and-suspenders pattern; addresses the Gmail
  // render where the cell's `bgcolor` alone wasn't sufficient.
  const cellStyle = `background-color: ${bgcolor}; border-radius: ${borderRadius}px;`
  const linkStyle = `display: inline-block; padding: ${paddingY}px ${paddingX}px; font-family: ${tokens.type.fontFamily}; font-size: ${tokens.type.size.sm}px; font-weight: ${tokens.type.weight.semibold}; color: ${textColor}; text-decoration: none; border-radius: ${borderRadius}px;`
  // Inner content (the <tr><td>...</td></tr>) is identical between the
  // two alignment modes; pre-build as an HtmlString and interpolate
  // into the per-mode outer table.
  // prettier-ignore
  const inner = html`<tr><td align="center" bgcolor="${bgcolor}" style="${cellStyle}"><a href="${absoluteHref}" target="_blank" style="${linkStyle}">${text}</a></td></tr>`
  if (align === 'center') {
    // Center-mode: `align="center"` HTML attr (Outlook Word engine) +
    // `margin: 0 auto;` inline style (modern clients). Both layers
    // needed — Outlook ignores CSS `margin: 0 auto` on block-level
    // tables; modern clients ignore the deprecated HTML `align` attr
    // for centering purposes (it falls back to text-align which
    // doesn't center block-level table children).
    // prettier-ignore
    return html`<table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0" style="margin: 0 auto;">${inner}</table>`
  }
  // Default left-aligned mode — no `align=` attr, no `margin` style;
  // shrink-wrapped table sits at the start of its parent cell.
  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0">${inner}</table>`
}
